import "./Form.scss";

import createFileList from "./helpers/createFileList";
import filterFileList from "./helpers/filterFileList";
import FileDialog from "./FileDialog";
import data from "./data";
import generateVideoPoster from "./helpers/generateVideoPoster";
import {
    ImageOptimizationError,
    isAllowedFileExtension,
    normalizeImageOptimizationSettings,
    optimizeImage
} from "./helpers/imageOptimization.mjs";

const PREPARATION_RENDER_BOUNDARY_TIMEOUT = 250;

/**
 * A class to wrap and extend the functionality of the given form to allow for better file uploading.
 */
export default class Form {
    /**
     * Create a new instance of the class.
     * @param {HTMLElement} form
     */
    constructor( form ) {
        this.onDragOver = this.onDragOver.bind( this );
        this.onDragDrop = this.onDragDrop.bind( this );
        this.onFilesChanged = this.onFilesChanged.bind( this );

        this.element = form;
        this.i18n = Object.assign( {}, data.i18n, JSON.parse( this.element.dataset.i18n ?? "{}" ) );
        this.dropZone = this.element.querySelector( ".fg-feu-drop-zone" );
        this.dropZone?.addEventListener( "dragover", this.onDragOver );
        this.dropZone?.addEventListener( "drop", this.onDragDrop );
        this.fileInput = this.element.querySelector( "input[name='foogallery_images[]']" );
        this.fileInput?.addEventListener( "change", this.onFilesChanged );
        this.fields = ( this.element.dataset.fields?.split( " " ) ?? [] ).filter( f => typeof f === "string" && f.length > 0 );
        this.requiredFields = ( this.element.dataset.requiredFields?.split( " " ) ?? [] ).filter( f => typeof f === "string" && f.length > 0 );
        this.maxSize = parseInt( this.element.dataset.maxSize ?? 0 ) * 1024 * 1024;
        this.maxImages = parseInt( this.element.dataset.maxImages ?? 0 );
        this.allowedTypes = ( this.element.dataset.allowedTypes?.split( "," ) ?? [] )
            .map( type => type.trim().toLowerCase() )
            .filter( Boolean );
        this.allowVideoUploads = ( this.element.dataset.allowVideoUploads ?? ( data.allowVideoUploads ? "yes" : "no" ) ) === "yes";
        this.videoFeatureAvailable = ( this.element.dataset.videoFeatureAvailable ?? ( data.videoFeatureAvailable ? "yes" : "no" ) ) === "yes";
        this.videoUploadsEnabled = ( this.element.dataset.videoUploadsEnabled ?? "no" ) === "yes";
        this.imageOptimization = normalizeImageOptimizationSettings( {
            enabled: this.element.dataset.imageOptimization ?? "no",
            quality: this.element.dataset.imageOptimizationQuality,
            maxWidth: this.element.dataset.imageOptimizationMaxWidth,
            format: this.element.dataset.imageOptimizationFormat
        } );
        this.preparationId = 0;
        this.preparationController = null;
        this.uploadItems = [];
        this.dialog = null;
        this.accessGate = null;
    }

    get hasAnyFiles() {
        return this.uploadItems.length > 0;
    }

    get hasSingleFile() {
        return this.uploadItems.length === 1;
    }

    get hasFields() {
        return this.fields.length > 0;
    }

    get hasValidUploadItems() {
        return this.uploadItems.some( item => !item.error );
    }

    get files() {
        return this.uploadItems.map( item => item.file );
    }

    async upload( formData, refreshAccess = false ) {
        try {
            let nonce = data.rest.nonce;
            if ( this.accessGate ) {
                // Protected galleries use fresh credentials from the access endpoint.
                const access = await this.accessGate.credentials( refreshAccess );
                formData.set( "access_csrf", access.csrf );
                nonce = access.nonce || nonce;
            }
            const response= await fetch( data.rest.endpoint, {
                method: "POST",
                credentials: "same-origin",
                headers: {
                    "X-WP-Nonce": nonce
                },
                body: formData
            } );
            const contentType = response.headers.get( "content-type" );
            if ( !contentType || !contentType.includes( "application/json" ) ) {
                return { success: false, message: this.i18n.restBadResponse };
            }
            const result = await response.json();
            if ( this.accessGate && !response.ok ) {
                if ( result.code === "upload_access_required" ) return { success: false, accessRequired: true };
                if ( result.code === "upload_csrf_failed" && !refreshAccess ) return this.upload( formData, true );
            }
            return !response.ok ? Object.assign( result, { success: false } ) : result;
        } catch ( err ) {
            return { success: false, message: this.i18n.restNetworkError, error: err };
        }
    }

    releaseUploadItem( item ) {
        if (
            item &&
            typeof item.previewUrl === "string" &&
            item.previewUrl.startsWith( "blob:" ) &&
            typeof URL !== "undefined" &&
            typeof URL.revokeObjectURL === "function"
        ) {
            try {
                URL.revokeObjectURL( item.previewUrl );
            } catch ( error ) {
                // Preview cleanup must not interrupt removal or cancellation.
            }
        }
    }

    createPreviewUrl( file ) {
        if ( typeof URL === "undefined" || typeof URL.createObjectURL !== "function" ) {
            return "";
        }

        try {
            const previewUrl = URL.createObjectURL( file );
            return typeof previewUrl === "string" ? previewUrl : "";
        } catch ( error ) {
            return "";
        }
    }

    clearUploadItems() {
        this.uploadItems.forEach( item => this.releaseUploadItem( item ) );
        this.uploadItems = [];
    }

    setUploadItems( items ) {
        const nextItems = Array.isArray( items ) ? items : [];
        this.uploadItems
            .filter( item => !nextItems.includes( item ) )
            .forEach( item => this.releaseUploadItem( item ) );
        this.uploadItems = nextItems;
        if ( this.fileInput instanceof HTMLInputElement ) {
            this.fileInput.files = createFileList( this.uploadItems.map( item => item.file ) );
        }
    }

    removeUploadItem( index ) {
        if ( index < 0 || index >= this.uploadItems.length ) {
            return;
        }

        const [ removed ] = this.uploadItems.splice( index, 1 );
        this.releaseUploadItem( removed );

        if ( this.fileInput instanceof HTMLInputElement ) {
            this.fileInput.files = createFileList( this.uploadItems.map( item => item.file ) );
        }
    }

    getFileExtension( file ) {
        return file.name.split( "." ).pop()?.toLowerCase() ?? "";
    }

    getFileKind( file ) {
        const extension = this.getFileExtension( file );
        const imageExtensions = [ "jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "avif", "heic", "heif" ];

        if ( extension === "mp4" || file.type === "video/mp4" || file.type.startsWith( "video/" ) ) {
            return "video";
        }

        if ( file.type.startsWith( "image/" ) || imageExtensions.includes( extension ) ) {
            return "image";
        }

        return "image";
    }

    isAllowedFileType( file ) {
        if ( !this.allowedTypes.length ) return true;
        const extension = this.getFileExtension( file );
        return extension.length > 0 && isAllowedFileExtension( extension, this.allowedTypes );
    }

    isSupportedFile( file ) {
        const kind = this.getFileKind( file );
        const extension = this.getFileExtension( file );

        if ( kind === "video" ) {
            return this.videoUploadsEnabled && this.isAllowedFileType( file ) && this.getFileExtension( file ) === "mp4";
        }

        return this.isAllowedFileType( file ) && ( file.type.startsWith( "image/" ) || [ "jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "avif", "heic", "heif" ].includes( extension ) );
    }

    async createUploadItem( file, signal = null ) {
        const kind = this.getFileKind( file );

        if ( kind === "video" && this.videoUploadsEnabled ) {
            try {
                const poster = await generateVideoPoster( file, signal );
                return {
                    file,
                    kind,
                    posterFile: poster.posterFile,
                    previewUrl: poster.previewUrl,
                    dimensions: {
                        width: poster.width,
                        height: poster.height
                    },
                    error: ""
                };
            } catch ( error ) {
                return {
                    file,
                    kind,
                    posterFile: null,
                    previewUrl: "",
                    dimensions: null,
                    error: this.i18n.videoPosterFailed
                };
            }
        }

        if ( kind === "image" && this.imageOptimization.enabled ) {
            try {
                const optimized = await optimizeImage( file, this.imageOptimization, { signal } );
                if ( !this.isAllowedFileType( optimized.file ) ) {
                    const extension = this.getFileExtension( optimized.file ).toUpperCase();
                    return {
                        file,
                        kind,
                        posterFile: null,
                        previewUrl: this.createPreviewUrl( file ),
                        dimensions: null,
                        error: this.i18n.imageOptimizationTypeNotAllowed.replace( "%s", extension ),
                        optimizationError: true
                    };
                }

                return {
                    file: optimized.file,
                    kind,
                    originalFileName: optimized.originalFileName,
                    posterFile: null,
                    previewUrl: this.createPreviewUrl( optimized.file ),
                    dimensions: optimized.dimensions,
                    error: ""
                };
            } catch ( error ) {
                let message = this.i18n.imageOptimizationUnavailable;
                const cancelled = error instanceof ImageOptimizationError && error.code === "cancelled";
                if ( error instanceof ImageOptimizationError ) {
                    if ( error.code === "decode-failed" ) {
                        message = this.i18n.imageOptimizationDecodeFailed;
                    } else if ( error.code === "unsupported-format" ) {
                        message = this.i18n.imageOptimizationFormatUnsupported;
                    }
                }

                return {
                    file,
                    kind,
                    posterFile: null,
                    previewUrl: cancelled || ( error instanceof ImageOptimizationError && error.code === "decode-failed" )
                        ? ""
                        : this.createPreviewUrl( file ),
                    dimensions: null,
                    error: message,
                    optimizationError: true
                };
            }
        }

        return {
            file,
            kind,
            posterFile: null,
            previewUrl: this.createPreviewUrl( file ),
            dimensions: null,
            error: ""
        };
    }

    async prepareUploadItems( fileList, onProgress = null ) {
        this.preparationController?.abort();
        const preparationController = typeof AbortController === "function"
            ? new AbortController()
            : {
                abort() {},
                signal: {
                    aborted: false,
                    addEventListener() {},
                    removeEventListener() {}
                }
            };
        this.preparationController = preparationController;
        const { signal } = preparationController;
        const preparationId = ++this.preparationId;
        this.clearUploadItems();
        const files = Array.from( fileList ?? [] );
        const uploadItems = [];
        let completed = 0;
        const isStale = () => signal.aborted || preparationId !== this.preparationId;
        const reportProgress = () => {
            if ( isStale() || typeof onProgress !== "function" ) return;
            try {
                onProgress( completed, files.length, preparationId );
            } catch ( error ) {
                // UI progress must never interrupt preparation or cleanup.
            }
        };

        try {
            if ( this.imageOptimization.enabled ) {
                for ( const file of files ) {
                    const uploadItem = await this.createUploadItem( file, signal );
                    if ( isStale() ) {
                        this.releaseUploadItem( uploadItem );
                        uploadItems.forEach( item => this.releaseUploadItem( item ) );
                        return null;
                    }
                    uploadItems.push( uploadItem );
                    completed++;
                    reportProgress();
                }
            } else {
                const results = await Promise.all( files.map( async file => {
                    try {
                        const uploadItem = await this.createUploadItem( file, signal );
                        if ( isStale() ) {
                            this.releaseUploadItem( uploadItem );
                            return { status: "fulfilled", value: null };
                        }
                        completed++;
                        reportProgress();
                        return { status: "fulfilled", value: uploadItem };
                    } catch ( reason ) {
                        completed++;
                        reportProgress();
                        return { status: "rejected", reason };
                    }
                } ) );

                const prepared = results
                    .filter( result => result.status === "fulfilled" && result.value !== null )
                    .map( result => result.value );
                if ( isStale() ) {
                    prepared.forEach( item => this.releaseUploadItem( item ) );
                    return null;
                }

                const failure = results.find( result => result.status === "rejected" );
                if ( failure ) {
                    prepared.forEach( item => this.releaseUploadItem( item ) );
                    throw failure.reason;
                }
                uploadItems.push( ...prepared );
            }

            if ( isStale() ) {
                uploadItems.forEach( item => this.releaseUploadItem( item ) );
                return null;
            }
            this.uploadItems = uploadItems;
            return this.uploadItems;
        } catch ( error ) {
            uploadItems.forEach( item => this.releaseUploadItem( item ) );
            if ( isStale() ) return null;
            throw error;
        } finally {
            if ( this.preparationController === preparationController ) {
                this.preparationController = null;
            }
        }
    }

    reset() {
        this.preparationController?.abort();
        this.preparationController = null;
        this.preparationId++;
        if ( this.fileInput instanceof HTMLInputElement ) {
            this.fileInput.value = null;
        }
        this.clearUploadItems();
    }

    getPreparationDetails( files ) {
        const hasVideo = files.some( file => this.getFileKind( file ) === "video" );
        const needsVideoPreparation = hasVideo && this.videoUploadsEnabled;
        const shouldShow = ( this.imageOptimization.enabled && files.some( file => this.getFileKind( file ) === "image" ) ) || needsVideoPreparation;
        return {
            shouldShow,
            message: hasVideo ? this.i18n.filePreparationPreparing : this.i18n.imageOptimizationPreparing,
            total: files.length
        };
    }

    waitForPreparationRenderBoundary() {
        return new Promise( resolve => {
            let settled = false;
            let deadlineId = null;
            let deadlineScheduled = false;
            let taskTarget = null;
            let setTask = null;
            let clearTask = null;

            const settle = () => {
                if ( settled ) return;
                settled = true;
                if ( deadlineScheduled && typeof clearTask === "function" ) {
                    try {
                        clearTask.call( taskTarget, deadlineId );
                    } catch ( error ) {
                        // A hostile timer implementation must not prevent settlement.
                    }
                }
                resolve();
            };
            const captureTasks = preferredTarget => {
                for ( const target of [ preferredTarget, globalThis ] ) {
                    if ( !target ) continue;
                    try {
                        const candidate = target.setTimeout;
                        if ( typeof candidate !== "function" ) continue;
                        taskTarget = target;
                        setTask = candidate;
                        try {
                            clearTask = target.clearTimeout;
                        } catch ( error ) {
                            clearTask = null;
                        }
                        return;
                    } catch ( error ) {
                        // Try the next safe task source.
                    }
                }
            };
            const scheduleTaskFallback = () => {
                if ( settled ) return;
                if ( typeof setTask === "function" ) {
                    try {
                        setTask.call( taskTarget, settle, 0 );
                        return;
                    } catch ( error ) {
                        // A microtask is the final fail-safe when no task can be queued.
                    }
                }
                Promise.resolve().then( settle );
            };

            let view = null;
            let requestFrame = null;
            try {
                const ownerDocument = this.element?.ownerDocument;
                view = ownerDocument?.defaultView;
                requestFrame = view?.requestAnimationFrame;
            } catch ( error ) {
                captureTasks( view );
                scheduleTaskFallback();
                return;
            }

            captureTasks( view );
            if ( typeof requestFrame !== "function" ) {
                scheduleTaskFallback();
                return;
            }

            if ( typeof setTask !== "function" ) {
                scheduleTaskFallback();
                return;
            }
            try {
                deadlineId = setTask.call( taskTarget, settle, PREPARATION_RENDER_BOUNDARY_TIMEOUT );
                deadlineScheduled = true;
            } catch ( error ) {
                scheduleTaskFallback();
                return;
            }

            let firstFrameRan = false;
            const requestNextFrame = callback => {
                if ( settled ) return;
                try {
                    requestFrame.call( view, callback );
                } catch ( error ) {
                    scheduleTaskFallback();
                }
            };

            requestNextFrame( () => {
                if ( settled || firstFrameRan ) return;
                firstFrameRan = true;
                requestNextFrame( settle );
            } );
        } );
    }

    isCurrentDialogGeneration( dialog, preparationId ) {
        return preparationId === this.preparationId && this.dialog === dialog;
    }

    isDialogConnected( dialog ) {
        try {
            return dialog?.element?.isConnected === true;
        } catch ( error ) {
            return false;
        }
    }

    cleanupDisconnectedDialog( dialog, preparationId ) {
        if ( !this.isCurrentDialogGeneration( dialog, preparationId ) || this.isDialogConnected( dialog ) ) {
            return false;
        }

        const controller = this.preparationController;
        this.preparationController = null;
        this.preparationId++;
        this.dialog = null;

        try {
            controller?.abort();
        } catch ( error ) {
            // Continue releasing the detached generation's remaining resources.
        }
        try {
            if ( this.fileInput instanceof HTMLInputElement ) this.fileInput.value = null;
        } catch ( error ) {
            // A hostile file input must not prevent resource cleanup.
        }
        try {
            this.clearUploadItems();
        } catch ( error ) {
            this.uploadItems = [];
        }

        try { dialog.isPreparing = false; } catch ( error ) {}
        try { dialog.preparationMessage = ""; } catch ( error ) {}
        try { dialog.preparationTotal = 0; } catch ( error ) {}
        try { dialog.empty(); } catch ( error ) {}
        try { dialog._firstOnlyValues.clear(); } catch ( error ) {}

        let ownerDocument = null;
        try {
            ownerDocument = dialog?.element?.ownerDocument;
        } catch ( error ) {
            try {
                ownerDocument = this.element?.ownerDocument;
            } catch ( nestedError ) {
                ownerDocument = null;
            }
        }
        try {
            ownerDocument?.documentElement?.classList?.remove( "fg-feu-dialog-open" );
        } catch ( error ) {
            // Scroll-lock cleanup is best-effort for a damaged document.
        }
        try { dialog?.element?.remove(); } catch ( error ) {}
        try { dialog.releaseFocus(); } catch ( error ) {}
        return true;
    }

    async onFilesChanged() {
        if ( !this.fileInput || !this.fileInput.files || this.fileInput.files.length === 0 ) {
            return null;
        }

        const files = Array.from( this.fileInput.files );
        if ( this.dialog === null ) this.dialog = new FileDialog( this );
        const dialog = this.dialog;
        if ( files.length === 0 ) {
            dialog.openPreparation( this.i18n.filePreparationPreparing, 0 );
            dialog.showPreparationError( this.i18n.noFilesToPrepare );
            return dialog;
        }

        const details = this.getPreparationDetails( files );
        const expectedPreparationId = this.preparationId + 1;
        if ( details.shouldShow ) {
            dialog.openPreparation( details.message, details.total );
        } else {
            dialog.clearSelectionState();
        }

        try {
            const uploadItems = await this.prepareUploadItems( files, ( completed, total, preparationId ) => {
                if ( preparationId === this.preparationId && preparationId === expectedPreparationId && this.dialog === dialog ) {
                    dialog.updatePreparationProgress( completed, total );
                }
            } );
            if ( uploadItems === null || !this.isCurrentDialogGeneration( dialog, expectedPreparationId ) ) return null;
            if ( details.shouldShow ) {
                await this.waitForPreparationRenderBoundary();
                if ( !this.isCurrentDialogGeneration( dialog, expectedPreparationId ) ) return null;
                if ( !this.isDialogConnected( dialog ) ) {
                    this.cleanupDisconnectedDialog( dialog, expectedPreparationId );
                    return null;
                }
                if ( !dialog.isPreparing ) return null;
            }
            if ( uploadItems.length === 0 ) {
                if ( !details.shouldShow ) dialog.openPreparation( this.i18n.filePreparationPreparing, 0 );
                dialog.showPreparationError( this.i18n.noFilesToPrepare );
                if ( !this.isDialogConnected( dialog ) ) {
                    this.cleanupDisconnectedDialog( dialog, expectedPreparationId );
                    return null;
                }
                return this.isCurrentDialogGeneration( dialog, expectedPreparationId ) ? dialog : null;
            }

            if ( details.shouldShow ) dialog.showPreparedItems();
            else dialog.open();
            if ( !this.isDialogConnected( dialog ) ) {
                this.cleanupDisconnectedDialog( dialog, expectedPreparationId );
                return null;
            }
            return this.isCurrentDialogGeneration( dialog, expectedPreparationId ) ? dialog : null;
        } catch ( error ) {
            if ( !this.isCurrentDialogGeneration( dialog, expectedPreparationId ) ) return null;
            if ( !this.isDialogConnected( dialog ) ) {
                this.cleanupDisconnectedDialog( dialog, expectedPreparationId );
                return null;
            }
            if ( !details.shouldShow ) dialog.openPreparation( this.i18n.filePreparationPreparing, files.length );
            dialog.showPreparationError( this.i18n.filePreparationFailed );
            if ( !this.isDialogConnected( dialog ) ) {
                this.cleanupDisconnectedDialog( dialog, expectedPreparationId );
                return null;
            }
            return this.isCurrentDialogGeneration( dialog, expectedPreparationId ) ? dialog : null;
        }
    }

    onDragOver( event ) {
        if ( event.dataTransfer.items.length > 0 ) {
            event.preventDefault();
        }
    }

    onDragDrop( event ) {
        const dropped = filterFileList( event.dataTransfer.files, file => this.isSupportedFile( file ) );
        if ( event.dataTransfer.files.length > 0 ) {
            event.preventDefault();
        }
        if ( dropped.length > 0 ) {
            this.fileInput.files = dropped;
            this.onFilesChanged();
        }
    }
};

//region type-defs

/**
 * @typedef {object} BadResponse
 * @property {false} success
 * @property {string} message
 */

/**
 * @typedef {object} NetworkErrorResponse
 * @property {false} success
 * @property {string} message
 * @property {Error} error
 */

/**
 * @typedef {object} WPErrorResponse
 * @property {false} success
 * @property {string} message
 * @property {string|int} code
 * @property {object} data
 */

/**
 * @typedef {{ status: ("succeeded" | "errored" | "skipped"), name: string, message: string }} FileResult
 * @property {("succeeded" | "errored" | "skipped")} status
 * @property {string} name
 * @property {string} message
 */

/**
 * @typedef {object} SuccessResponse
 * @property {true} success
 * @property {string} message
 * @property {FileResult[]} files
 * @property {number} succeeded
 * @property {number} errored
 * @property {number} skipped
 */

/**
 * @typedef {SuccessResponse|WPErrorResponse|BadResponse|NetworkErrorResponse} UploadResponse
 */

//endregion
